import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { sampleMembers } from '../data/membersData';
import './WorldMap.css';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const WorldMap = () => {
  const [tooltipContent, setTooltipContent] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Group members by region for country mapping
  const countryCounts = {
    UAE: 0,
    KSA: 0,
    Qatar: 0,
    Oman: 0,
    Kuwait: 0,
    India: 0,
    UK: 0,
    Indonesia: 0,
    Canada: 0,
    Germany: 0
  };

  sampleMembers.forEach(member => {
    const loc = member.location;
    if (loc === 'UAE') countryCounts.UAE++;
    else if (['Riyadh', 'Jeddah', 'Jubail', 'Dammam'].includes(loc)) countryCounts.KSA++;
    else if (loc === 'Qatar') countryCounts.Qatar++;
    else if (loc === 'Oman') countryCounts.Oman++;
    else if (loc === 'Kuwait') countryCounts.Kuwait++;
    else if (loc === 'Pandikkad') countryCounts.India++;
    else if (loc === 'UK') countryCounts.UK++;
    else if (loc === 'Indonesia') countryCounts.Indonesia++;
    else if (loc === 'Canada') countryCounts.Canada++;
    else if (loc === 'Germany') countryCounts.Germany++;
  });

  const getMembersForCountry = (country) => {
    return sampleMembers.filter(member => {
      const loc = member.location;
      if (country === 'UAE') return loc === 'UAE';
      if (country === 'KSA') return ['Riyadh', 'Jeddah', 'Jubail', 'Dammam'].includes(loc);
      if (country === 'Qatar') return loc === 'Qatar';
      if (country === 'Oman') return loc === 'Oman';
      if (country === 'Kuwait') return loc === 'Kuwait';
      if (country === 'India') return loc === 'Pandikkad';
      if (country === 'UK') return loc === 'UK';
      if (country === 'Indonesia') return loc === 'Indonesia';
      if (country === 'Canada') return loc === 'Canada';
      if (country === 'Germany') return loc === 'Germany';
      return false;
    });
  };

  const markers = [
    { markerOffset: -15, name: "UAE", coordinates: [54.3644, 24.1302], count: countryCounts.UAE, code: "🇦🇪" },
    { markerOffset: 15, name: "KSA", coordinates: [45.0792, 23.8859], count: countryCounts.KSA, code: "🇸🇦" },
    { markerOffset: -15, name: "Qatar", coordinates: [51.1839, 25.3548], count: countryCounts.Qatar, code: "🇶🇦" },
    { markerOffset: 15, name: "Oman", coordinates: [55.9232, 21.4735], count: countryCounts.Oman, code: "🇴🇲" },
    { markerOffset: -15, name: "Kuwait", coordinates: [47.4818, 29.3117], count: countryCounts.Kuwait, code: "🇰🇼" },
    { markerOffset: 15, name: "India", coordinates: [78.9629, 20.5937], count: countryCounts.India, code: "🇮🇳" },
    { markerOffset: -15, name: "Germany", coordinates: [10.4515, 51.1657], count: countryCounts.Germany, code: "🇩🇪" },
    { markerOffset: -15, name: "UK", coordinates: [-3.4360, 55.3781], count: countryCounts.UK, code: "🇬🇧" },
    { markerOffset: 15, name: "Indonesia", coordinates: [113.9213, -0.7893], count: countryCounts.Indonesia, code: "🇮🇩" },
    { markerOffset: -15, name: "Canada", coordinates: [-106.3468, 56.1304], count: countryCounts.Canada, code: "🇨🇦" }
  ].filter(m => m.count > 0);

  return (
    <div className="world-map-wrapper">
      <div className="tooltip" style={{ display: tooltipContent ? 'block' : 'none' }}>
        {tooltipContent}
      </div>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 140,
        }}
        width={800}
        height={400}
        style={{ 
          width: "100%", 
          height: "auto", 
          backgroundColor: "#f0f8ff", 
          borderRadius: "15px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)"
        }}
      >
        <ZoomableGroup>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#E2E8F0"
                  stroke="#CBD5E1"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#CBD5E1", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
          
          {markers.map(({ name, coordinates, markerOffset, count, code }) => (
            <Marker key={name} coordinates={coordinates}>
              <g
                fill="none"
                stroke="#FF5533"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform="translate(-12, -24)"
                onMouseEnter={() => setTooltipContent(`${code} ${name}: ${count} members`)}
                onMouseLeave={() => setTooltipContent('')}
                onClick={() => setSelectedCountry(selectedCountry === name ? null : name)}
                style={{ cursor: 'pointer', pointerEvents: 'all' }}
              >
                <circle cx="12" cy="10" r="3" />
                <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 6 8 11.7z" />
              </g>
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      {selectedCountry && (
        <div className="selected-country-members">
          <h3>Members in {selectedCountry} ({markers.find(m => m.name === selectedCountry)?.count})</h3>
          <div className="country-members-grid">
            {getMembersForCountry(selectedCountry).map(member => (
              <div key={member.id} className="country-member-card">
                <span className="member-name">{member.name}</span>
                <span className="member-loc">📍 {member.location}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldMap;
